import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';

export interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
}

type Resolve = (v: boolean) => void;

const ConfirmContext = React.createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [state, setState] = React.useState<{ opts: ConfirmOptions; resolve: Resolve } | null>(null);

  const ask = React.useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => setState({ opts, resolve }));
  }, []);

  function decide(value: boolean) {
    if (!state) return;
    state.resolve(value);
    setState(null);
  }

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => !o && decide(false)}>
        <DialogContent className="max-w-md">
          {state && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-3">
                  {state.opts.variant === 'destructive' && (
                    <div className="rounded-full bg-destructive/10 p-2 shrink-0">
                      <AlertTriangle className="h-5 w-5 text-destructive" />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <DialogTitle>{state.opts.title}</DialogTitle>
                    {state.opts.body && <DialogDescription>{state.opts.body}</DialogDescription>}
                  </div>
                </div>
              </DialogHeader>
              <DialogFooter className="gap-2 mt-2">
                <Button variant="outline" onClick={() => decide(false)}>
                  {state.opts.cancelLabel ?? t('common.cancel')}
                </Button>
                <Button
                  variant={state.opts.variant === 'destructive' ? 'destructive' : 'default'}
                  onClick={() => decide(true)}
                  autoFocus
                >
                  {state.opts.confirmLabel ?? t('common.ok')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be inside ConfirmProvider');
  return ctx;
}

// Helper for delete flows — pre-fills destructive variant and the common "Delete" label
export function useConfirmDelete() {
  const ask = useConfirm();
  const { t } = useTranslation();
  return React.useCallback(
    (title: string, body?: string) =>
      ask({
        title,
        body,
        variant: 'destructive',
        confirmLabel: t('common.delete', { defaultValue: 'Delete' }),
      }),
    [ask, t],
  );
}

// Optional: tiny inline-loader confirm for async work
export function ConfirmingButton({
  loading,
  children,
}: {
  loading: boolean;
  children: React.ReactNode;
}) {
  return loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{children}</>;
}
